/**
 * VisuelAllureAM.jsx — Carte HTML/CSS "Adapter son allure" spécifique Permis AM.
 *
 * Remplace le visuel PNG masqué (qui montrait 50/80 km/h) par une infographie
 * fidèle au style de la série AM_THEMES : entête coloré, N° 5, badge marque,
 * bloc central "45 km/h max", 3 pastilles (Sécurité · Risques · À retenir).
 *
 * Deux modes de rendu :
 *   • variant="thumb"    → format vignette (dans la grille de la galerie)
 *   • variant="focus"    → format grand (dans la modale focus)
 *
 * Cohérence :
 *   - Fond blanc, textes noirs (comme les 11 autres visuels)
 *   - Palette rouge/vert/jaune identique aux autres cartes
 *   - Format 4:3 pour matcher les vignettes
 */
import React from 'react';
import { Gauge, ShieldCheck, AlertTriangle, Lightbulb } from 'lucide-react';

export function VisuelAllureAM({ variant = 'thumb' }) {
  const isFocus = variant === 'focus';

  // ─── Version vignette (grille) : compacte pour tenir en aspect 4/3 ───
  if (!isFocus) {
    return (
      <div className="w-full h-full bg-white text-slate-900 overflow-hidden flex flex-col" data-testid="visuel-allure-am">
        {/* Bandeau haut */}
        <div className="relative bg-gradient-to-r from-rose-600 via-rose-500 to-red-500 px-3 py-2 flex items-center gap-2 shrink-0">
          <div className="shrink-0 w-8 h-8 rounded-full bg-white text-rose-600 font-black flex items-center justify-center text-base shadow">5</div>
          <div className="min-w-0 flex-1 text-white">
            <div className="font-black uppercase tracking-wide text-[13px] leading-none">Adapter son allure</div>
            <div className="opacity-95 text-[10px] leading-tight mt-0.5">Spécial Permis AM</div>
          </div>
          <div className="shrink-0 bg-white rounded-md px-1.5 py-0.5 shadow">
            <span className="text-rose-600 font-black text-[9px]">CODE</span>
            <span className="text-slate-900 font-black text-[9px]">2</span>
            <span className="text-rose-600 font-black text-[9px]">ROUES</span>
          </div>
        </div>

        {/* Corps compact : gros chiffre + 3 mini-badges */}
        <div className="flex-1 flex flex-col justify-center items-center px-3 py-2 gap-2 bg-gradient-to-b from-white via-white to-rose-50/40">
          {/* Chiffre 45 km/h */}
          <div className="flex items-center gap-2.5">
            <div className="w-14 h-14 rounded-full bg-white border-4 border-rose-600 flex items-center justify-center font-black text-rose-600 text-lg shadow">45</div>
            <div className="text-left leading-tight">
              <div className="font-black text-slate-900 text-2xl leading-none">45 <span className="text-rose-600">km/h</span></div>
              <div className="font-black uppercase tracking-widest text-rose-600 text-[8.5px] mt-0.5">Vitesse max · AM</div>
              <div className="text-slate-600 text-[9px]">Par construction · partout</div>
            </div>
          </div>
          {/* Mini-badges */}
          <div className="flex items-center gap-1.5 mt-1">
            <span className="px-1.5 py-0.5 rounded-full bg-emerald-50 border border-emerald-300 text-emerald-700 text-[8.5px] font-black uppercase tracking-wider">Sécurité</span>
            <span className="px-1.5 py-0.5 rounded-full bg-rose-50 border border-rose-300 text-rose-700 text-[8.5px] font-black uppercase tracking-wider">Risques</span>
            <span className="px-1.5 py-0.5 rounded-full bg-amber-50 border border-amber-300 text-amber-700 text-[8.5px] font-black uppercase tracking-wider">À retenir</span>
          </div>
        </div>
      </div>
    );
  }

  // ─── Version focus (modale) : mise en page complète et généreuse ───
  return (
    <div className="w-full bg-white text-slate-900 overflow-hidden" data-testid="visuel-allure-am">
      {/* Bandeau haut coloré */}
      <div className="relative bg-gradient-to-r from-rose-600 via-rose-500 to-red-500 px-6 py-4">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-14 h-14 rounded-full bg-white text-rose-600 font-black flex items-center justify-center text-2xl shadow-md">5</div>
          <div className="min-w-0 flex-1 text-white">
            <div className="font-black uppercase tracking-wider leading-tight text-2xl sm:text-3xl">Adapter son allure</div>
            <div className="opacity-95 text-base sm:text-lg mt-1 leading-snug">Rouler à la bonne vitesse — spécial Permis AM</div>
          </div>
          <div className="shrink-0 flex flex-col items-end">
            <div className="bg-white rounded-md px-2 py-1 shadow">
              <span className="text-rose-600 font-black text-[11px]">CODE</span>
              <span className="text-slate-900 font-black text-[11px]">2</span>
              <span className="text-rose-600 font-black text-[11px]">ROUES</span>
            </div>
            <div className="mt-1 text-[9px] font-black tracking-widest text-white/90">PERMIS AM · N°5</div>
          </div>
        </div>
      </div>

      <div className="px-6 py-5 space-y-5">
        {/* Bloc central : 45 km/h max */}
        <div className="rounded-2xl border-4 border-rose-500 bg-rose-50 text-center p-5">
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="w-24 h-24 rounded-full bg-white border-4 border-rose-600 flex items-center justify-center font-black text-rose-600 text-3xl shadow">45</div>
            <div className="text-left">
              <div className="font-black leading-none text-slate-900 text-5xl sm:text-6xl">45 <span className="text-rose-600">km/h</span></div>
              <div className="font-bold uppercase tracking-widest text-rose-600 text-sm mt-2">Vitesse max · voiturette + cyclomoteur</div>
            </div>
          </div>
          <p className="text-slate-700 text-base leading-relaxed mt-2">
            Le Permis AM est limité à <strong>45 km/h par construction</strong>.
            Cette vitesse maximale s'applique <strong>partout</strong> — même sur route ouverte
            où les voitures peuvent rouler à 80 km/h.
          </p>
        </div>

        {/* 3 pastilles */}
        <div className="grid grid-cols-3 gap-4">
          <div className="rounded-lg bg-emerald-50 border-2 border-emerald-300 p-3">
            <div className="flex items-center gap-1 font-black text-emerald-700 uppercase tracking-wider text-xs mb-2">
              <ShieldCheck className="w-4 h-4" />
              <span>Pour ta sécurité</span>
            </div>
            <ul className="text-slate-800 leading-tight text-sm space-y-1.5">
              <li>Roule à ton max de 45</li>
              <li>Adapte-toi à la météo</li>
              <li>Ralentis en virage</li>
              <li>Distance de sécurité</li>
            </ul>
          </div>
          <div className="rounded-lg bg-rose-50 border-2 border-rose-300 p-3">
            <div className="flex items-center gap-1 font-black text-rose-700 uppercase tracking-wider text-xs mb-2">
              <AlertTriangle className="w-4 h-4" />
              <span>Les risques</span>
            </div>
            <ul className="text-slate-800 leading-tight text-sm space-y-1.5">
              <li>Débridage = illégal</li>
              <li>Voitures qui te doublent</li>
              <li>Vitesse sur sol mouillé</li>
              <li>Freiner trop tard</li>
            </ul>
          </div>
          <div className="rounded-lg bg-amber-50 border-2 border-amber-300 p-3">
            <div className="flex items-center gap-1 font-black text-amber-700 uppercase tracking-wider text-xs mb-2">
              <Lightbulb className="w-4 h-4" />
              <span>À retenir</span>
            </div>
            <div className="text-slate-900 font-bold leading-tight text-base">
              45 km/h, <span className="text-amber-700">c'est TA</span> limite — <span className="text-rose-700">observe, anticipe, laisse passer</span>.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VisuelAllureAM;
